/**
 * MapView.tsx — Mapa compartido (F1).
 *
 * Extraído de `MapPage` para que el portal del inversionista, el detalle de
 * módulo y el mapa GIS no repitan la lógica de Leaflet. **Regla del proyecto:
 * este es el único fichero del frontend que importa `leaflet` fuera de
 * `MapPage`** — ninguna página nueva debe hacerlo directamente.
 *
 * Pinta features (lotes o módulos) como polígono + marcador, coloreadas por
 * estado, y opcionalmente deja trazar una geocerca a mano.
 */

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import type { GeoJSONPolygon } from '../../types/api.types';
import {
  labelDetailForZoom, labelAnchor, buildLabelLines, estimateLabelSize, placeLabels, DEFAULT_LABEL_FIELDS,
  type LabelFields, type LabelSource, type LabelCandidate,
} from '../../utils/map-labels';
import { edgeMidpoints, insertMidpoint, removeVertex, translatePoints, MIN_VERTICES } from '../../utils/polygon-edit';

// Fix del icono por defecto que rompe el bundler de Vite
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({ iconUrl, iconRetinaUrl, shadowUrl });

/** Estados de módulo de F1 (`terrain.model.ts`). */
export type FeatureStatus = 'PREPARACION' | 'NORMAL' | 'ATENCION' | 'CRITICO' | 'COSECHANDO';

/**
 * Un color por estado, exportado para que la leyenda de cada página use
 * exactamente el mismo que el polígono — si vivieran en dos lados, el mapa y su
 * leyenda acabarían diciendo cosas distintas.
 */
export const STATUS_COLORS: Record<FeatureStatus, string> = {
  PREPARACION: '#757575', // gris — sin sembrar todavía
  NORMAL:      '#388E3C', // verde
  ATENCION:    '#F57F17', // ámbar
  CRITICO:     '#C62828', // rojo
  COSECHANDO:  '#1565C0', // azul
};

const DEFAULT_COLOR = '#388E3C';
const SELECTED_WEIGHT = 4;
const BASE_WEIGHT = 2;

// Los lotes ya no se colorean por status (eso es de los módulos) — llevan un
// contorno neutro y relleno casi nulo para que los módulos de adentro se
// sigan viendo con claridad (F1: "unificar módulos con lotes" — ambos se
// pintan siempre juntos, el lote es el contenedor, el módulo es la subdivisión).
const LOTE_COLOR = '#424242';
const LOTE_BASE_WEIGHT = 3;
const LOTE_FILL = 0.04;
const LOTE_FILL_SELECTED = 0.15;
const MODULE_FILL = 0.2;
const MODULE_FILL_SELECTED = 0.35;
// Un lote con relleno explícito (tema de etapa / color elegido) es visible
// por sí mismo — un lote sin zonas es su propia zona.
const LOTE_FILL_COLORED = 0.2;
const LOTE_FILL_COLORED_SELECTED = 0.35;

const FADED_FILL_FACTOR = 0.4;
const FADED_STROKE_OPACITY = 0.5;

/** Opacidad de relleno de un polígono según tipo, selección, color propio y ciclo cerrado. */
export function fillOpacityFor(f: Pick<MapFeature, 'kind' | 'fillColor' | 'faded'> | undefined, selected: boolean): number {
  let base: number;
  if (f?.kind === 'lote') {
    if (f.fillColor) base = selected ? LOTE_FILL_COLORED_SELECTED : LOTE_FILL_COLORED;
    else base = selected ? LOTE_FILL_SELECTED : LOTE_FILL;
  } else {
    base = selected ? MODULE_FILL_SELECTED : MODULE_FILL;
  }
  return f?.faded ? base * FADED_FILL_FACTOR : base;
}

/** Opacidad del borde: atenuado para un ciclo cerrado. */
export function strokeOpacityFor(f: Pick<MapFeature, 'faded'> | undefined): number {
  return f?.faded ? FADED_STROKE_OPACITY : 1;
}

const DEFAULT_CENTER: [number, number] = [22.15, -100.98]; // SLP, México
const DEFAULT_ZOOM = 12;

// Debajo de este zoom, lotes/módulos se resumen en burbujas de cluster con
// conteo en vez de dibujar cada polígono — a nivel región se vuelven
// ilegibles y lentos de pintar. En o por encima, se ven las geocercas reales.
const SHOW_POLYGONS_ZOOM = DEFAULT_ZOOM;

const OSM_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const OSM_ATTRIBUTION = '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
// Esri World Imagery: la capa satélite que pide el portal para ver el cultivo
const SATELLITE_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const SATELLITE_ATTRIBUTION = 'Tiles © Esri';

const DRAW_STYLE = { color: '#F57F17', weight: 2, dashArray: '6 4', fillOpacity: 0.1 };

// Un color distinto por vértice — así se distingue cada punto del polígono
// a simple vista en vez de que todos se vean iguales. Exportado para que el
// listado de vértices en el panel lateral use exactamente los mismos colores.
export const VERTEX_COLORS = ['#E91E63', '#3F51B5', '#009688', '#FF9800', '#9C27B0', '#00BCD4', '#8BC34A', '#F44336'];
export const vertexColor = (i: number) => VERTEX_COLORS[i % VERTEX_COLORS.length];

/**
 * Área geodésica aproximada de un polígono (fórmula esférica de Leaflet
 * GeometryUtil) — evita jalar la librería completa solo por esta función.
 */
export function geodesicAreaHa(points: [number, number][]): number {
  if (points.length < 3) return 0;
  const R = 6378137; // radio de la Tierra en metros
  let area = 0;
  const len = points.length;
  for (let i = 0; i < len; i++) {
    const [lat1, lng1] = points[i];
    const [lat2, lng2] = points[(i + 1) % len];
    area += ((lng2 - lng1) * Math.PI / 180)
      * (2 + Math.sin(lat1 * Math.PI / 180) + Math.sin(lat2 * Math.PI / 180));
  }
  area = Math.abs((area * R * R) / 2);
  return area / 10000; // m² → ha
}

/**
 * Ray casting sobre un anillo [lat,lng] — ¿el punto cae dentro del polígono?
 * Exportada para poder probarla sin pasar por Leaflet: es lo que decide,
 * al hacer click, qué lotes/módulos se superponen en ese punto.
 */
export function polygonContainsPoint(point: [number, number], ring: [number, number][]): boolean {
  const [py, px] = point;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [yi, xi] = ring[i];
    const [yj, xj] = ring[j];
    const intersects = (yi > py) !== (yj > py)
      && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

/** Centroide simple (promedio de vértices) — suficiente como punto representativo para el cluster, no hace falta el área-ponderado. */
function ringCentroid(ring: [number, number][]): [number, number] {
  const pts = ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
    ? ring.slice(0, -1)
    : ring;
  const sum = pts.reduce(([sy, sx], [y, x]) => [sy + y, sx + x], [0, 0]);
  return [sum[0] / pts.length, sum[1] / pts.length];
}

function clusterIcon(cluster: L.MarkerCluster): L.DivIcon {
  const count = cluster.getChildCount();
  const size = count < 10 ? 34 : count < 100 ? 42 : 50;
  return L.divIcon({
    className: 'agrovid-cluster-icon',
    html: `<div style="
      width:${size}px;height:${size}px;border-radius:50%;
      background:${DEFAULT_COLOR};color:#fff;
      display:flex;align-items:center;justify-content:center;
      font-weight:600;font-size:${count < 100 ? 13 : 12}px;
      border:2px solid rgba(255,255,255,0.85);
      box-shadow:0 1px 4px rgba(0,0,0,0.4);
    ">${count}</div>`,
    iconSize: L.point(size, size),
  });
}

function vertexIcon(color: string, selected: boolean): L.DivIcon {
  const size = selected ? 22 : 14;
  const border = selected ? '3px solid #FFEB3B' : '2px solid white';
  return L.divIcon({
    className: 'agrovid-vertex-marker',
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:${border};box-shadow:0 1px 3px rgba(0,0,0,0.6);"></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function midpointIcon(): L.DivIcon {
  return L.divIcon({
    className: 'agrovid-mid-marker',
    html: '<div style="width:10px;height:10px;border-radius:50%;background:rgba(255,255,255,0.9);border:2px solid #F57F17;cursor:copy;"></div>',
    iconSize: [10, 10],
    iconAnchor: [5, 5],
  });
}

function selectedPinIcon(color: string): L.DivIcon {
  return L.divIcon({
    className: 'agrovid-selected-pin',
    html: `<div style="width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${color};border:4px solid #FFEB3B;box-shadow:0 2px 6px rgba(0,0,0,0.6);"></div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
  });
}

export interface MapFeature {
  id: string;
  name: string;
  latitude?: number;
  longitude?: number;
  boundary?: GeoJSONPolygon;
  status?: FeatureStatus;
  /** HTML del popup; si falta se usa el nombre */
  popupHtml?: string;
  /** 'lote' pinta contorno neutro sin relleno por status; default 'module'. */
  kind?: 'lote' | 'module';
  /**
   * Color de relleno explícito — pisa el color por status (STATUS_COLORS).
   * Usado por la capa NDVI del mapa avanzado (MapPage.tsx): cuando está
   * activa, el relleno pasa a representar NDVI en vez de status operativo.
   */
  fillColor?: string;
  /**
   * Color/patrón de borde explícitos — independientes del relleno, usados
   * por el checkerboard de cultivo (ver utils/crop-checkerboard.ts): el
   * borde señala cultivo aunque el relleno esté mostrando status o NDVI.
   */
  strokeColor?: string;
  strokeDashArray?: string;
  /** Último ciclo cerrado (D16): relleno y borde atenuados. */
  faded?: boolean;
  /** Datos para la capa de etiquetas (ver utils/map-labels.ts). */
  label?: LabelSource;
}

export interface MapViewProps {
  features: MapFeature[];
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  /** Capa satélite en vez del mapa de calles */
  satellite?: boolean;
  /** Modo trazar: cada click agrega un vértice */
  drawMode?: boolean;
  /** Puntos trazados en [lat, lng] — el padre decide qué hacer con ellos */
  onDrawPointsChange?: (points: [number, number][]) => void;
  /** Si true, drawMode captura un único punto (reemplaza el anterior) en vez de trazar un polígono. */
  singlePointMode?: boolean;
  /** Centro/zoom inicial (solo al montar) para cuando no hay features que encuadrar. */
  initialCenter?: [number, number];
  initialZoom?: number;
  /** Recentra el mapa (con zoom) cuando cambia; opcional y sin efecto si no se pasa. */
  recenterTo?: { center: [number, number]; zoom?: number; bounds?: [[number, number], [number, number]] } | null;
  /**
   * Clave de encuadre: el mapa solo llama fitBounds cuando esta clave cambia
   * (o la primera vez que llegan features). Así guardar/editar una geocerca,
   * que refresca la lista de features, no resetea el zoom del usuario.
   */
  fitKey?: string | number | null;
  /** Anillo [lat,lng] fuera del cual no se permiten vértices (zona dentro de zona o de lote). */
  drawBounds?: [number, number][] | null;
  /** Vértices [lat,lng] con los que arranca el modo trazar (editar geocerca existente). Se leen al activar drawMode. */
  initialDrawPoints?: [number, number][];
  /** Capa de etiquetas flotantes en el centro de cada lote/zona. */
  showLabels?: boolean;
  labelFields?: LabelFields;
  /**
   * Repetición de un vuelo de dron: ruta planeada (tenue), tramo ya recorrido/rociado y posición
   * actual del dron. Todo en [lat, lng]; el padre avanza `sprayed` y `drone` con el tiempo.
   */
  track?: { planned: [number, number][]; sprayed: [number, number][]; drone?: [number, number] | null };
  className?: string;
  style?: React.CSSProperties;
}

function colorFor(f?: Pick<MapFeature, 'status' | 'kind'>): string {
  if (f?.kind === 'lote') return LOTE_COLOR;
  return f?.status ? STATUS_COLORS[f.status] ?? DEFAULT_COLOR : DEFAULT_COLOR;
}

/** Relleno: `fillColor` explícito (capa NDVI) pisa el color por status. */
function fillColorFor(f?: Pick<MapFeature, 'status' | 'kind' | 'fillColor'>): string {
  return f?.fillColor ?? colorFor(f);
}

/** Borde: `strokeColor` explícito (checkerboard de cultivo) pisa el color por status. */
function strokeColorFor(f?: Pick<MapFeature, 'status' | 'kind' | 'strokeColor'>): string {
  return f?.strokeColor ?? colorFor(f);
}

function hasRing(boundary?: GeoJSONPolygon): boolean {
  return !!boundary?.coordinates?.length && boundary.coordinates[0].length > 0;
}

export function MapView({
  features,
  selectedId,
  onSelect,
  satellite = false,
  drawMode = false,
  onDrawPointsChange,
  singlePointMode = false,
  className,
  style,
  initialCenter,
  initialZoom,
  recenterTo,
  fitKey,
  initialDrawPoints,
  drawBounds,
  showLabels = false,
  labelFields = DEFAULT_LABEL_FIELDS,
  track,
}: MapViewProps) {
  const drawBoundsRef = useRef(drawBounds);
  useEffect(() => { drawBoundsRef.current = drawBounds; }, [drawBounds]);
  const insideBounds = (pt: [number, number]) => {
    const b = drawBoundsRef.current;
    return !b || b.length < 3 || polygonContainsPoint(pt, b);
  };
  const mapElRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const tileRef = useRef<L.TileLayer | null>(null);
  // Polígonos (lotes/módulos con geocerca) — solo viven en el mapa cuando el
  // zoom está por encima de SHOW_POLYGONS_ZOOM; abajo de eso se resumen como
  // puntos dentro de clusterGroupRef.
  const layersRef = useRef<Map<string, L.Polygon>>(new Map());
  // Anillos [lat,lng] de cada polígono, siempre poblados (independiente del
  // zoom) — es lo que el click handler usa para detectar superposiciones.
  const polygonRingsRef = useRef<Map<string, [number, number][]>>(new Map());
  const featuresRef = useRef<MapFeature[]>(features);
  const onSelectRef = useRef(onSelect);
  const zoomRef = useRef(DEFAULT_ZOOM);
  const clusterGroupRef = useRef<L.MarkerClusterGroup | null>(null);
  // Pines (L.marker) por feature — para poder cambiar su ícono al seleccionar.
  const featureMarkersRef = useRef<Map<string, L.Marker[]>>(new Map());
  const drawLayerRef = useRef<L.Polygon | null>(null);
  const drawPtsRef = useRef<[number, number][]>([]);
  const vertexMarkersRef = useRef<L.Marker[]>([]);
  const selectedVertexRef = useRef<number | null>(null);
  const drawModeRef = useRef(drawMode);
  const initialDrawPointsRef = useRef(initialDrawPoints);
  const overlapPopupRef = useRef<L.Popup | null>(null);
  const labelMarkersRef = useRef<L.Marker[]>([]);
  const [currentZoom, setCurrentZoom] = useState(DEFAULT_ZOOM);

  const trackFitKeyRef = useRef('');
  const trackLayersRef = useRef<{ planned: L.Polyline; sprayed: L.Polyline; drone: L.Marker } | null>(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!track) {
      if (trackLayersRef.current) { Object.values(trackLayersRef.current).forEach(l => l.remove()); trackLayersRef.current = null; }
      return;
    }
    if (!trackLayersRef.current) {
      const droneIcon = L.divIcon({
        className: 'agrovid-drone-marker',
        html: '<div style="width:22px;height:22px;border-radius:50%;background:#ffffff;border:3px solid #1b7a3d;box-shadow:0 0 0 4px rgba(61,220,132,.45),0 2px 6px rgba(0,0,0,.6);"></div>',
        iconSize: [22, 22], iconAnchor: [11, 11],
      });
      trackLayersRef.current = {
        planned: L.polyline([], { color: '#ffffff', weight: 2, opacity: 0.55, dashArray: '4 6' }).addTo(map),
        sprayed: L.polyline([], { color: '#3ddc84', weight: 5, opacity: 0.9 }).addTo(map),
        drone: L.marker([0, 0], { icon: droneIcon, interactive: false, zIndexOffset: 1000 }),
      };
    }
    const layers = trackLayersRef.current;
    layers.planned.setLatLngs(track.planned);
    layers.sprayed.setLatLngs(track.sprayed);
    // Al empezar un vuelo se acerca la cámara a su trayectoria para que el trazo se vea bien.
    const key = track.planned.length ? `${track.planned[0]}|${track.planned.length}` : '';
    if (key && key !== trackFitKeyRef.current) {
      trackFitKeyRef.current = key;
      const bounds = layers.planned.getBounds();
      window.setTimeout(() => { if (mapRef.current === map) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 19, animate: true }); }, 350);
    }
    if (track.drone) { layers.drone.setLatLng(track.drone); if (!map.hasLayer(layers.drone)) layers.drone.addTo(map); }
    else layers.drone.remove();
  }, [track]);

  useEffect(() => { featuresRef.current = features; }, [features]);
  useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);
  useEffect(() => { zoomRef.current = currentZoom; }, [currentZoom]);
  useEffect(() => { drawModeRef.current = drawMode; }, [drawMode]);
  useEffect(() => { initialDrawPointsRef.current = initialDrawPoints; }, [initialDrawPoints]);

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapElRef.current || mapRef.current) return;

    const map = L.map(mapElRef.current, { center: initialCenter ?? DEFAULT_CENTER, zoom: initialZoom ?? DEFAULT_ZOOM, maxZoom: 19 });
    mapRef.current = map;

    const clusterGroup = L.markerClusterGroup({
      iconCreateFunction: clusterIcon,
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
    });
    clusterGroup.addTo(map);
    clusterGroupRef.current = clusterGroup;

    map.on('zoomend', () => setCurrentZoom(map.getZoom()));

    return () => { map.remove(); mapRef.current = null; clusterGroupRef.current = null; };
  }, []);

  // ── Recentrado a demanda (ej. "Usar mi ubicación") ────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!recenterTo || !map) return;
    // `bounds` encuadra la zona completa con el zoom adecuado; sin él, solo recentra.
    if (recenterTo.bounds) map.fitBounds(recenterTo.bounds, { padding: [48, 48], maxZoom: 18 });
    else map.setView(recenterTo.center, recenterTo.zoom ?? map.getZoom());
  }, [recenterTo]);

  // ── Capa base (se cambia sin recrear el mapa) ─────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (tileRef.current) map.removeLayer(tileRef.current);
    tileRef.current = L.tileLayer(
      satellite ? SATELLITE_URL : OSM_URL,
      { attribution: satellite ? SATELLITE_ATTRIBUTION : OSM_ATTRIBUTION, maxZoom: 19 },
    ).addTo(map);
  }, [satellite]);

  // Bounds de encuadre — a propósito NO dependen del zoom (solo de qué
  // features llegaron), para no pelearse con el usuario cada vez que hace
  // zoom manual (si dependieran de currentZoom, cada zoomend dispararía un
  // fitBounds que lo regresaría a la vista completa).
  const featureBounds = useMemo<L.LatLngTuple[]>(() => {
    const b: L.LatLngTuple[] = [];
    for (const f of features) {
      if (hasRing(f.boundary)) {
        f.boundary!.coordinates[0].forEach(([lng, lat]) => b.push([lat, lng]));
      }
      if (f.latitude != null && f.longitude != null) b.push([f.latitude, f.longitude]);
    }
    return b;
  }, [features]);

  // Solo encuadra la primera vez que hay datos o cuando cambia `fitKey` (ej.
  // rancho activo) — nunca por un simple refresco de features (guardar, editar).
  const fittedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !featureBounds.length) return;
    const key = String(fitKey ?? '');
    if (fittedKeyRef.current === key) return;
    fittedKeyRef.current = key;
    map.fitBounds(L.latLngBounds(featureBounds), { padding: [40, 40], maxZoom: 15 });
  }, [featureBounds, fitKey]);

  // ── Features ──────────────────────────────────────────────────────────────
  // Depende también de currentZoom: por debajo de SHOW_POLYGONS_ZOOM los
  // polígonos se resumen como puntos en el clusterGroup (burbuja con conteo);
  // en o por encima se dibujan como geocerca real y el click entre varias que
  // se superpongan lo resuelve el handler de abajo vía polygonRingsRef.
  useEffect(() => {
    const map = mapRef.current;
    const clusterGroup = clusterGroupRef.current;
    if (!map || !clusterGroup) return;

    layersRef.current.forEach(layer => map.removeLayer(layer));
    layersRef.current.clear();
    polygonRingsRef.current.clear();
    featureMarkersRef.current.clear();
    clusterGroup.clearLayers();

    const showPolygons = currentZoom >= SHOW_POLYGONS_ZOOM;
    const trackMarker = (id: string, m: L.Marker) => {
      const list = featureMarkersRef.current.get(id) ?? [];
      list.push(m);
      featureMarkersRef.current.set(id, list);
    };

    for (const f of features) {
      const isLote = f.kind === 'lote';
      const popup = f.popupHtml ?? `<b>${f.name}</b>`;

      if (hasRing(f.boundary)) {
        const coords = f.boundary!.coordinates[0].map(
          ([lng, lat]) => [lat, lng] as [number, number],
        );
        polygonRingsRef.current.set(f.id, coords);

        if (showPolygons) {
          // Sin bindPopup ni click propio: la selección (incluida la lista
          // cuando varios polígonos se superponen en el punto clickeado) la
          // resuelve el handler unificado de abajo vía polygonRingsRef.
          const poly = L.polygon(coords, {
            color: strokeColorFor(f),
            fillColor: fillColorFor(f),
            ...(f.strokeDashArray ? { dashArray: f.strokeDashArray } : {}),
            weight: isLote ? LOTE_BASE_WEIGHT : BASE_WEIGHT,
            fillOpacity: fillOpacityFor(f, false),
            opacity: strokeOpacityFor(f),
          })
            .bindTooltip(f.name)
            .addTo(map);

          layersRef.current.set(f.id, poly);
        } else {
          const [lat, lng] = ringCentroid(coords);
          const marker = L.marker([lat, lng]).bindPopup(popup)
            .on('click', () => onSelectRef.current?.(f.id));
          clusterGroup.addLayer(marker);
          trackMarker(f.id, marker);
        }
      }

      if (f.latitude != null && f.longitude != null) {
        const marker = L.marker([f.latitude, f.longitude]).bindPopup(popup)
          .on('click', () => onSelectRef.current?.(f.id));

        clusterGroup.addLayer(marker);
        trackMarker(f.id, marker);
      }
    }
  }, [features, currentZoom]);

  // ── Etiquetas flotantes ───────────────────────────────────────────────────
  // Se recalculan por zoom (los píxeles cambian) y al cambiar features/campos.
  // Solo con polígonos visibles; no interceptan clicks (interactive: false).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    labelMarkersRef.current.forEach(m => map.removeLayer(m));
    labelMarkersRef.current = [];

    const detail = showLabels ? labelDetailForZoom(currentZoom) : 'none';
    if (detail === 'none' || currentZoom < SHOW_POLYGONS_ZOOM) return;

    const candidates: LabelCandidate[] = [];
    const linesById = new Map<string, string[]>();
    for (const f of features) {
      if (!hasRing(f.boundary)) continue;
      const lines = buildLabelLines(f.label ?? { name: f.name }, labelFields, detail);
      if (!lines.length) continue;
      const ring = f.boundary!.coordinates[0].map(([lng, lat]) => [lat, lng] as [number, number]);
      const pts = ring.map(([lat, lng]) => map.project(L.latLng(lat, lng), currentZoom));
      // Zonas contenidas en esta (hijas): la etiqueta se ancla fuera de ellas.
      const holes = features
        .filter(o => o.id !== f.id && hasRing(o.boundary)
          && geodesicAreaHa(o.boundary!.coordinates[0].map(([lng, lat]) => [lat, lng] as [number, number]))
            < geodesicAreaHa(ring)
          && polygonContainsPoint(ringCentroid(o.boundary!.coordinates[0].map(([lng, lat]) => [lat, lng] as [number, number])), ring))
        .map(o => o.boundary!.coordinates[0].map(([lng, lat]) => map.project(L.latLng(lat, lng), currentZoom)));
      const c = labelAnchor(pts, holes) ?? (() => {
        const [clat, clng] = ringCentroid(ring);
        return map.project(L.latLng(clat, clng), currentZoom);
      })();
      const xs = pts.map(p => p.x);
      const ys = pts.map(p => p.y);
      const { w, h } = estimateLabelSize(lines);
      linesById.set(f.id, lines);
      candidates.push({
        id: f.id, x: c.x, y: c.y, w, h,
        priority: f.kind === 'lote' ? 1 : 2,
        polyW: Math.max(...xs) - Math.min(...xs),
        polyH: Math.max(...ys) - Math.min(...ys),
      });
    }

    const esc = (v: string) => v.replace(/[&<>"]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch] as string));
    for (const p of placeLabels(candidates)) {
      const lines = linesById.get(p.id) ?? [];
      const ll = map.unproject(L.point(p.x, p.y), currentZoom);
      const marker = L.marker(ll, {
        interactive: false,
        keyboard: false,
        icon: L.divIcon({
          className: 'agrovid-map-label',
          html: `<div style="transform:translate(-50%,-50%);white-space:nowrap;text-align:center;font:600 11px/14px sans-serif;`
            + `color:#111;text-shadow:0 0 3px #fff,0 0 3px #fff,0 0 2px #fff;pointer-events:none;">`
            + lines.map(esc).join('<br/>') + '</div>',
          iconSize: [0, 0],
        }),
      }).addTo(map);
      labelMarkersRef.current.push(marker);
    }
  }, [features, showLabels, labelFields, currentZoom]);

  // ── Resaltar la selección ─────────────────────────────────────────────────
  useEffect(() => {
    layersRef.current.forEach((layer, id) => {
      const feature = features.find(f => f.id === id);
      const selected = id === selectedId;
      const isLote = feature?.kind === 'lote';

      layer.setStyle({
        color: strokeColorFor(feature),
        fillColor: fillColorFor(feature),
        ...(feature?.strokeDashArray ? { dashArray: feature.strokeDashArray } : {}),
        weight: selected ? SELECTED_WEIGHT : (isLote ? LOTE_BASE_WEIGHT : BASE_WEIGHT),
        fillOpacity: fillOpacityFor(feature, selected),
        opacity: strokeOpacityFor(feature),
      });
    });
  }, [selectedId, features]);

  // Pin del feature seleccionado: ícono más grande con borde de realce; el
  // resto vuelve al ícono por defecto. Corre después del efecto de features
  // (que recrea los marcadores) y al cambiar la selección, sin redibujar todo.
  useEffect(() => {
    featureMarkersRef.current.forEach((markers, id) => {
      const selected = id === selectedId;
      const feature = features.find(f => f.id === id);
      markers.forEach(m => {
        m.setIcon?.(selected ? selectedPinIcon(fillColorFor(feature)) : new L.Icon.Default());
        m.setZIndexOffset?.(selected ? 2000 : 0);
      });
    });
  }, [selectedId, features, currentZoom]);

  // ── Modo trazar ───────────────────────────────────────────────────────────
  // Arrastrar la geocerca completa: mousedown/touchstart sobre el boceto lo
  // traslada entero (todos los vértices y puntos medios siguen al cursor).
  // El mapa no debe paneo-arse mientras tanto, así que el evento se detiene
  // en el propio elemento SVG y el arrastre del mapa se pausa. Si el puntero
  // no se movió (un simple clic) no se hace nada y el clic sigue su curso
  // normal (agregar vértice); si sí se movió, se suprime el clic posterior.
  const suppressClickRef = useRef(false);
  const onDrawPointsChangeRef = useRef(onDrawPointsChange);
  useEffect(() => { onDrawPointsChangeRef.current = onDrawPointsChange; }, [onDrawPointsChange]);

  function attachPolygonDrag(layer: L.Polygon) {
    const map = mapRef.current;
    const el = layer.getElement?.() as SVGElement | undefined;
    if (!map || !el) return;
    el.style.cursor = 'move';
    el.style.touchAction = 'none';

    const pointer = (ev: MouseEvent | TouchEvent) => {
      const p = 'touches' in ev ? (ev.touches[0] ?? ev.changedTouches[0]) : ev;
      return map.mouseEventToLatLng({ clientX: p.clientX, clientY: p.clientY } as MouseEvent);
    };

    const onStart = (ev: Event) => {
      if (!drawModeRef.current) return;
      if (ev instanceof MouseEvent && ev.button !== 0) return;
      L.DomEvent.stop(ev);
      const e0 = ev as MouseEvent | TouchEvent;
      const start = pointer(e0);
      const startPx = map.latLngToContainerPoint(start);
      const origin = drawPtsRef.current.map(p => [p[0], p[1]] as [number, number]);
      let moved = false;
      map.dragging.disable();

      const onMove = (e: MouseEvent | TouchEvent) => {
        const cur = pointer(e);
        if (!moved) {
          const px = map.latLngToContainerPoint(cur);
          if (Math.hypot(px.x - startPx.x, px.y - startPx.y) < 4) return; // umbral: un clic no es arrastre
          moved = true;
        }
        if (e.cancelable) e.preventDefault();
        const pts = translatePoints(origin, cur.lat - start.lat, cur.lng - start.lng);
        if (!pts.every(insideBounds)) return; // no sale de la zona/lote contenedor
        drawPtsRef.current = pts;
        layer.setLatLngs(pts);
        vertexMarkersRef.current.forEach((m, i) => { if (pts[i]) m.setLatLng(pts[i]); });
        edgeMidpoints(pts).forEach((pt, i) => midMarkersRef.current[i]?.setLatLng(pt));
      };
      const onEnd = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('touchmove', onMove);
        window.removeEventListener('mouseup', onEnd);
        window.removeEventListener('touchend', onEnd);
        window.removeEventListener('touchcancel', onEnd);
        map.dragging.enable();
        if (!moved) return;
        suppressClickRef.current = true;
        setTimeout(() => { suppressClickRef.current = false; }, 50);
        onDrawPointsChangeRef.current?.([...drawPtsRef.current]);
        rebuildRef.current(); // los puntos medios se recalculan
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('touchmove', onMove, { passive: false });
      window.addEventListener('mouseup', onEnd);
      window.addEventListener('touchend', onEnd);
      window.addEventListener('touchcancel', onEnd);
    };

    L.DomEvent.on(el as unknown as HTMLElement, 'mousedown touchstart', onStart);
  }

  const redrawPolygon = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    if (drawLayerRef.current) { map.removeLayer(drawLayerRef.current); drawLayerRef.current = null; }
    if (drawPtsRef.current.length >= 2) {
      const layer = L.polygon(drawPtsRef.current, DRAW_STYLE).addTo(map);
      drawLayerRef.current = layer;
      if (drawPtsRef.current.length >= MIN_VERTICES) {
        // Con >= 3 vértices el boceto es un polígono real: va al frente y se
        // puede arrastrar completo (los vértices siguen siendo arrastrables).
        layer.bringToFront?.();
        attachPolygonDrag(layer);
      } else {
        layer.bringToBack();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- attachPolygonDrag solo lee refs
  }, []);

  const setVertexIcon = useCallback((marker: L.Marker, index: number) => {
    const selected = selectedVertexRef.current === index;
    marker.setIcon(vertexIcon(vertexColor(index), selected));
    marker.setZIndexOffset(selected ? 1000 : 0);
  }, []);

  const selectVertex = useCallback((index: number | null) => {
    selectedVertexRef.current = selectedVertexRef.current === index ? null : index;
    vertexMarkersRef.current.forEach((m, i) => setVertexIcon(m, i));
  }, [setVertexIcon]);

  const midMarkersRef = useRef<L.Marker[]>([]);

  const clearVertexMarkers = useCallback(() => {
    const map = mapRef.current;
    vertexMarkersRef.current.forEach(m => map?.removeLayer(m));
    midMarkersRef.current.forEach(m => map?.removeLayer(m));
    vertexMarkersRef.current = [];
    midMarkersRef.current = [];
    selectedVertexRef.current = null;
  }, []);

  // `rebuildRef` rompe la dependencia circular: los marcadores (que disparan
  // inserciones/borrados) y `rebuildDrawMarkers` (que los crea) se refieren entre si.
  const rebuildRef = useRef<() => void>(() => {});

  const commitPoints = useCallback((pts: [number, number][], selectIndex: number | null = null) => {
    drawPtsRef.current = pts;
    onDrawPointsChange?.([...pts]);
    rebuildRef.current();
    redrawPolygon();
    if (selectIndex != null) { selectedVertexRef.current = null; selectVertex(selectIndex); }
  }, [onDrawPointsChange, redrawPolygon, selectVertex]);

  // Cada vertice es un L.marker (no circleMarker — solo Marker soporta drag
  // nativo en Leaflet) con un divIcon circular; arrastrarlo mueve el punto
  // correspondiente en drawPtsRef y redibuja el poligono en vivo.
  const addVertexMarker = useCallback((pt: [number, number], index: number) => {
    const map = mapRef.current;
    if (!map) return;
    const marker = L.marker(pt, { icon: vertexIcon(vertexColor(index), false), draggable: true, autoPan: true });
    marker.on('drag', (e) => {
      const ll = (e.target as L.Marker).getLatLng();
      if (!insideBounds([ll.lat, ll.lng])) {
        // Fuera del contenedor: el vértice se queda en su última posición válida.
        const last = drawPtsRef.current[index];
        (e.target as L.Marker).setLatLng(last);
        return;
      }
      drawPtsRef.current[index] = [ll.lat, ll.lng];
      redrawPolygon();
    });
    marker.on('dragend', () => {
      onDrawPointsChange?.([...drawPtsRef.current]);
      rebuildRef.current(); // los puntos medios se recalculan
    });
    marker.on('click', (e) => {
      L.DomEvent.stopPropagation(e);
      selectVertex(index);
    });
    // Clic derecho: borra el vertice (minimo 3).
    marker.on('contextmenu', (e) => {
      L.DomEvent.stopPropagation(e);
      (e as any).originalEvent?.preventDefault?.();
      commitPoints(removeVertex(drawPtsRef.current, index));
    });
    marker.addTo(map);
    vertexMarkersRef.current.push(marker);
  }, [onDrawPointsChange, redrawPolygon, selectVertex, commitPoints]);

  // Marcador pequeno en el punto medio de cada arista: al clicarlo inserta un vertice ahi.
  const addMidMarker = useCallback((pt: [number, number], edgeIndex: number) => {
    const map = mapRef.current;
    if (!map) return;
    const marker = L.marker(pt, { icon: midpointIcon(), draggable: false });
    marker.on('click', (e) => {
      L.DomEvent.stopPropagation(e);
      const next = insertMidpoint(drawPtsRef.current, edgeIndex);
      if (!insideBounds(next[edgeIndex + 1])) return;
      commitPoints(next, edgeIndex + 1);
    });
    marker.addTo(map);
    midMarkersRef.current.push(marker);
  }, [commitPoints]);

  const rebuildDrawMarkers = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const keepSelected = selectedVertexRef.current;
    vertexMarkersRef.current.forEach(m => map.removeLayer(m));
    midMarkersRef.current.forEach(m => map.removeLayer(m));
    vertexMarkersRef.current = [];
    midMarkersRef.current = [];
    drawPtsRef.current.forEach((pt, i) => addVertexMarker(pt, i));
    if (drawPtsRef.current.length >= MIN_VERTICES) {
      edgeMidpoints(drawPtsRef.current).forEach((pt, i) => addMidMarker(pt, i));
    }
    selectedVertexRef.current = keepSelected != null && keepSelected < drawPtsRef.current.length ? keepSelected : null;
    vertexMarkersRef.current.forEach((m, i) => setVertexIcon(m, i));
  }, [addVertexMarker, addMidMarker, setVertexIcon]);

  useEffect(() => { rebuildRef.current = rebuildDrawMarkers; }, [rebuildDrawMarkers]);

  const handleMapClick = useCallback((e: L.LeafletMouseEvent) => {
    // El clic que cierra un arrastre de geocerca no agrega un vértice.
    if (suppressClickRef.current) { suppressClickRef.current = false; return; }
    const pt: [number, number] = [e.latlng.lat, e.latlng.lng];
    const map = mapRef.current;
    if (!map) return;

    if (singlePointMode) {
      drawPtsRef.current = [pt];
      onDrawPointsChange?.([pt]);
      clearVertexMarkers();
      if (drawLayerRef.current) { map.removeLayer(drawLayerRef.current); drawLayerRef.current = null; }
      addVertexMarker(pt, 0);
      return;
    }

    if (!insideBounds(pt)) return;
    commitPoints([...drawPtsRef.current, pt]);
  }, [onDrawPointsChange, singlePointMode, addVertexMarker, clearVertexMarkers, commitPoints]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (drawMode) {
      map.on('click', handleMapClick);
      map.doubleClickZoom.disable();
      // Editar geocerca existente: arranca con los vertices guardados.
      const seed = initialDrawPointsRef.current;
      if (!singlePointMode && seed && seed.length > 0 && drawPtsRef.current.length === 0) {
        drawPtsRef.current = seed.map(p => [p[0], p[1]] as [number, number]);
        rebuildRef.current();
        redrawPolygon();
      }
    } else {
      map.off('click', handleMapClick);
      map.doubleClickZoom.enable();
      // Al salir del modo trazar se limpia el boceto: si quedara pintado, el
      // usuario creería que la geocerca se guardó.
      drawPtsRef.current = [];
      if (drawLayerRef.current) {
        map.removeLayer(drawLayerRef.current);
        drawLayerRef.current = null;
      }
      clearVertexMarkers();
    }

    return () => { map.off('click', handleMapClick); };
  }, [drawMode, handleMapClick, clearVertexMarkers, redrawPolygon, singlePointMode]);

  // Tecla Supr: borra el vertice seleccionado (minimo 3).
  useEffect(() => {
    if (!drawMode || singlePointMode) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== 'Delete') return;
      const tag = (ev.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      const idx = selectedVertexRef.current;
      if (idx == null) return;
      ev.preventDefault();
      commitPoints(removeVertex(drawPtsRef.current, idx));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawMode, singlePointMode, commitPoints]);

  // ── Click en zonas superpuestas ──────────────────────────────────────────
  // Los polígonos de Leaflet burbujean su click al mapa (bubblingMouseEvents
  // por defecto en Path), así que un único listener aquí basta para resolver
  // tanto el caso de un solo lote/módulo bajo el punto como el de varios
  // superpuestos (ej. un módulo dentro de su lote, o dos módulos vecinos que
  // comparten borde) — en ese caso se ofrece una lista para elegir cuál.
  const closeOverlapPopup = useCallback(() => {
    if (overlapPopupRef.current) {
      mapRef.current?.closePopup(overlapPopupRef.current);
      overlapPopupRef.current = null;
    }
  }, []);

  const handleOverlapClick = useCallback((e: L.LeafletMouseEvent) => {
    if (drawModeRef.current) return;
    const map = mapRef.current;
    if (!map || zoomRef.current < SHOW_POLYGONS_ZOOM) return;

    const point: [number, number] = [e.latlng.lat, e.latlng.lng];
    const matches: MapFeature[] = [];
    polygonRingsRef.current.forEach((ring, id) => {
      if (polygonContainsPoint(point, ring)) {
        const feature = featuresRef.current.find(f => f.id === id);
        if (feature) matches.push(feature);
      }
    });

    closeOverlapPopup();

    if (matches.length === 1) {
      onSelectRef.current?.(matches[0].id);
      return;
    }
    if (matches.length < 2) return;

    const container = document.createElement('div');
    container.className = 'agrovid-overlap-list';
    matches.forEach((f) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = f.name;
      btn.style.cssText = 'display:block;width:100%;text-align:left;padding:6px 10px;'
        + 'border:none;background:transparent;cursor:pointer;font:inherit;';
      btn.addEventListener('mouseenter', () => { btn.style.background = '#f0f0f0'; });
      btn.addEventListener('mouseleave', () => { btn.style.background = 'transparent'; });
      btn.addEventListener('click', () => {
        onSelectRef.current?.(f.id);
        closeOverlapPopup();
      });
      container.appendChild(btn);
    });

    overlapPopupRef.current = L.popup({ className: 'agrovid-overlap-popup' })
      .setLatLng(e.latlng)
      .setContent(container)
      .openOn(map);
  }, [closeOverlapPopup]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.on('click', handleOverlapClick);
    return () => { map.off('click', handleOverlapClick); };
  }, [handleOverlapClick]);

  return <div ref={mapElRef} className={className} style={{ width: '100%', height: '100%', ...style }} />;
}
